import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Marker, Popup } from '@vis.gl/react-maplibre';
import type { MapRef } from '@vis.gl/react-maplibre';
import { FiClock, FiMapPin, FiPhone, FiRefreshCw, FiUser } from 'react-icons/fi';
import MapLibreMap from '../../components/maps/MapLibreMap';
import {
  clientPortalService,
  type ClientPortalActiveGuard,
  type ClientPortalContext,
  type ClientPortalLivePosition,
  type ClientPortalPatrolSweep,
  type ClientPortalScope,
} from '../../services/clientPortalService';
import { useI18n } from '../../i18n';
import { formatDateTime, formatDuration, formatRelative } from './clientPortalFormat';
import './ClientPortal.css';

interface Props {
  context: ClientPortalContext;
  siteId: string | null;
  can: (scope: ClientPortalScope) => boolean;
}

const DEFAULT_VIEW = { latitude: 53.3498, longitude: -6.2603, zoom: 12 };
const REFRESH_INTERVAL_MS = 30_000;

export default function ClientLiveOpsPage({ context, siteId, can }: Props) {
  const { t } = useI18n();
  const mapRef = useRef<MapRef | null>(null);
  const [guards, setGuards] = useState<ClientPortalActiveGuard[]>([]);
  const [positions, setPositions] = useState<ClientPortalLivePosition[]>([]);
  const [sweeps, setSweeps] = useState<ClientPortalPatrolSweep[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefreshed, setLastRefreshed] = useState<string>(() => new Date().toISOString());

  const showMap = can('live_map');
  const showGuards = can('active_guards');
  const showPatrols = can('patrol_progress');

  const load = useCallback(async () => {
    setError('');
    try {
      const since = new Date(Date.now() - 12 * 60 * 60 * 1000);
      const [guardRows, positionRows, sweepRows] = await Promise.all([
        showGuards ? clientPortalService.fetchActiveGuards() : Promise.resolve([]),
        showMap ? clientPortalService.fetchLivePositions() : Promise.resolve([]),
        showPatrols
          ? clientPortalService.fetchPatrolProgress(since, new Date(), siteId)
          : Promise.resolve([]),
      ]);

      setGuards(siteId ? guardRows.filter((row) => row.siteId === siteId) : guardRows);
      setPositions(siteId ? positionRows.filter((row) => row.siteId === siteId) : positionRows);
      setSweeps(sweepRows);
      setLastRefreshed(new Date().toISOString());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [showGuards, showMap, showPatrols, siteId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Positions also arrive over realtime, but a slow poll covers the case where
  // a guard's shift ends and their row simply stops being visible to us.
  useEffect(() => {
    const timer = window.setInterval(() => { void load(); }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!showMap) return undefined;
    return clientPortalService.subscribeToLivePositions(() => { void load(); });
  }, [showMap, load]);

  const initialView = useMemo(() => {
    if (positions.length === 0) return DEFAULT_VIEW;
    return { latitude: positions[0].latitude, longitude: positions[0].longitude, zoom: 14 };
  }, [positions]);

  const selectedPosition = positions.find((row) => row.shiftExecutionId === selectedId) ?? null;
  const inProgressSweeps = sweeps.filter((sweep) => sweep.status === 'in_progress');

  const focusGuard = (guard: ClientPortalActiveGuard) => {
    const position = positions.find((row) => row.shiftExecutionId === guard.shiftExecutionId);
    if (!position) return;
    setSelectedId(guard.shiftExecutionId);
    mapRef.current?.flyTo({ center: [position.longitude, position.latitude], zoom: 16 });
  };

  if (isLoading) {
    return <div className="cp-loading"><div className="spinner" /></div>;
  }

  return (
    <div className="cp-page">
      {error ? <div className="cp-banner cp-banner--error">{error}</div> : null}

      <div className="cp-toolbar">
        <span className="cp-toolbar__note">
          {t('Updated {time}', { time: formatRelative(lastRefreshed) })}
        </span>
        <button type="button" className="btn btn--secondary btn--sm" onClick={() => void load()}>
          <FiRefreshCw aria-hidden="true" /> {t('Refresh')}
        </button>
      </div>

      {showMap ? (
        <section className="cp-map-panel">
          <div className="cp-map">
            <MapLibreMap
              ref={mapRef}
              initialViewState={initialView}
              style={{ height: '100%', width: '100%' }}
              onClick={() => setSelectedId(null)}
            >
              {positions.map((position) => (
                <Marker
                  key={position.shiftExecutionId}
                  latitude={position.latitude}
                  longitude={position.longitude}
                  anchor="bottom"
                >
                  <button
                    type="button"
                    className="cp-map-pin"
                    aria-label={position.guardName ?? t('Officer on duty')}
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedId(position.shiftExecutionId);
                    }}
                  >
                    <FiUser aria-hidden="true" />
                  </button>
                </Marker>
              ))}

              {selectedPosition ? (
                <Popup
                  latitude={selectedPosition.latitude}
                  longitude={selectedPosition.longitude}
                  anchor="top"
                  closeButton={false}
                  onClose={() => setSelectedId(null)}
                >
                  <div className="cp-map-popup">
                    <strong>{selectedPosition.guardName ?? t('Officer on duty')}</strong>
                    <span>{selectedPosition.siteName}</span>
                    <span>{t('Last ping {time}', { time: formatRelative(selectedPosition.updatedAt) })}</span>
                  </div>
                </Popup>
              ) : null}
            </MapLibreMap>
          </div>

          {positions.length === 0 ? (
            <div className="cp-map-overlay">
              {t('No live positions are being reported right now.')}
            </div>
          ) : null}
        </section>
      ) : (
        <div className="cp-notice">
          <FiMapPin aria-hidden="true" />
          <div>
            <strong>{t('Live GPS tracking is switched off')}</strong>
            <p>{t('Your security provider controls live tracking for this contract. Contact them if you need map visibility.')}</p>
          </div>
        </div>
      )}

      <div className="cp-grid cp-grid--2">
        <article className="cp-panel">
          <header className="cp-panel__head">
            <div>
              <h2>{t('Officers on duty')}</h2>
              <p>{t('Everyone currently clocked in at your sites.')}</p>
            </div>
            <span className="cp-count">{guards.length}</span>
          </header>

          {!showGuards ? (
            <div className="cp-empty">{t('Your security provider has not enabled on-duty visibility.')}</div>
          ) : guards.length === 0 ? (
            <div className="cp-empty">{t('No officers are clocked in right now.')}</div>
          ) : (
            <ul className="cp-list">
              {guards.map((guard) => {
                const hasPosition = positions.some((row) => row.shiftExecutionId === guard.shiftExecutionId);
                return (
                  <li key={guard.shiftExecutionId} className="cp-list__row">
                    <div>
                      <strong>{guard.guardName ?? t('Officer on duty')}</strong>
                      <span>
                        {guard.siteName}
                        {guard.currentSegmentLabel ? ` · ${guard.currentSegmentLabel}` : ''}
                      </span>
                      {guard.guardPhone ? (
                        <a className="cp-inline-link" href={`tel:${guard.guardPhone}`}>
                          <FiPhone aria-hidden="true" /> {guard.guardPhone}
                        </a>
                      ) : null}
                    </div>
                    <div className="cp-list__meta">
                      <span className="cp-chip cp-chip--live">{t('On duty')}</span>
                      <span>
                        <FiClock aria-hidden="true" />{' '}
                        {t('{duration} on site', {
                          duration: formatDuration(guard.actualClockInAt, null),
                        })}
                      </span>
                      {showMap && hasPosition ? (
                        <button type="button" className="cp-link" onClick={() => focusGuard(guard)}>
                          {t('Show on map')}
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </article>

        <article className="cp-panel">
          <header className="cp-panel__head">
            <div>
              <h2>{t('Patrols in progress')}</h2>
              <p>{t('Checkpoints hit as officers work through a tour.')}</p>
            </div>
            <span className="cp-count">{inProgressSweeps.length}</span>
          </header>

          {!showPatrols ? (
            <div className="cp-empty">{t('Your security provider has not enabled patrol visibility.')}</div>
          ) : inProgressSweeps.length === 0 ? (
            <div className="cp-empty">{t('No patrol is running at the moment.')}</div>
          ) : (
            <ul className="cp-list">
              {inProgressSweeps.map((sweep) => {
                const pct = sweep.checkpointsTotal > 0
                  ? Math.round((sweep.checkpointsCompleted / sweep.checkpointsTotal) * 100)
                  : 0;
                return (
                  <li key={sweep.segmentId} className="cp-list__row cp-list__row--stacked">
                    <div>
                      <strong>{sweep.patrolName ?? t('Patrol')}</strong>
                      <span>{sweep.siteName} · {sweep.guardName ?? t('Officer on duty')}</span>
                    </div>
                    <div className="cp-progress" role="img" aria-label={t('{done} of {total} checkpoints complete', { done: sweep.checkpointsCompleted, total: sweep.checkpointsTotal })}>
                      <div className="cp-progress__track">
                        <div className="cp-progress__fill" style={{ width: `${pct}%` }} />
                      </div>
                      <span>
                        {t('{done}/{total} checkpoints', {
                          done: sweep.checkpointsCompleted,
                          total: sweep.checkpointsTotal,
                        })}
                      </span>
                    </div>
                    <div className="cp-list__meta">
                      <span>{t('Started {time}', { time: formatDateTime(sweep.actualStartedAt) })}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </article>
      </div>

      {context.sites.length > 1 && !siteId ? (
        <p className="cp-footnote">
          {t('Showing every site on your contract. Use the site selector to narrow this view.')}
        </p>
      ) : null}
    </div>
  );
}
